import type { Stroke, StrokeRun } from "./stroke-shape.js";
import { placeSteps, shapeSteps } from "./stroke-shape.js";

/**
 * How unlike two strokes are.
 *
 * The whole of what a lookup does with a candidate is add this up over the
 * strokes the reader has drawn. `handwriting-lookup.ts` is the scan and the
 * ranking, and this is the arithmetic under it.
 */

/**
 * What each part of a comparison contributes to the distance between two
 * strokes.
 *
 * **Two of the four are asked of the whole stroke and two of each run.** Where
 * the stroke sits and how long it is are facts about the stroke; which way each
 * part of it goes and how far along it that part reaches are facts about its
 * shape. Splitting them that way is what makes a stroke the reader swept in one
 * motion comparable with one the outlines record in three.
 *
 * Asking a run where it sits was tried first and is a penalty on the stored
 * side of exactly that case. The runs of a 丿 have their middles either side of
 * the middle of the 丿, so a reader's single run was three cells away from both
 * of them and a straight 丨 in the same place was nought away. 大 came 38th
 * behind 下, 工 and 廾 under positional pairing and stayed fourth once the runs
 * were aligned. It is first with the position asked once.
 *
 * The weights were swept together over 250 characters written three ways, from
 * a steady hand to one whose strokes land a tenth of the character out of
 * place.
 */
const costWeights = {
  /** Which way one run goes. */
  direction: 1,

  /** How far along its own stroke one run reaches. */
  run: 0.3,

  /** Where the whole stroke sits. */
  place: 1.6,

  /** How long the whole stroke is, against the whole character. */
  stroke: 0.9,
} as const;

/** The most two directions can differ by, the grid being a ring. */
const halfTurn = shapeSteps / 2;

/** The most two places can differ by, corner to opposite corner. */
const furthestPlace = (placeSteps - 1) * 2;

/** How far apart two directions are, the short way round. */
function directionGap(written: number, stored: number): number {
  const gap = Math.abs(written - stored);

  return gap > halfTurn ? shapeSteps - gap : gap;
}

/**
 * How unlike one written run is to one stored run.
 *
 * Shape alone. The length is a share of the stroke the run belongs to, so a
 * reader's single sweep reads as all of its stroke and each third of a
 * recorded 竖弯钩 reads as a third of its own.
 */
function runCost(
  written: StrokeRun,
  writtenLength: number,
  stored: StrokeRun,
  storedLength: number,
): number {
  return (
    (directionGap(written.direction, stored.direction) / halfTurn) *
      costWeights.direction +
    Math.abs(written.length / writtenLength - stored.length / storedLength) *
      costWeights.run
  );
}

/**
 * The most runs either side of a comparison is allowed to bring to it.
 *
 * The tables below are allocated once at this size rather than per comparison,
 * a lookup making one per stroke per candidate and there being 9,276 of those.
 * Measured over the whole index, no stroke has more than five runs; the ceiling
 * is generous and a stroke over it is compared on the runs that fit, which is
 * a worse answer for one stroke rather than a wrong one for the character.
 */
const mostRuns = 8;

/** The running cost of the best path to each pairing. */
const pathCosts = new Float64Array(mostRuns * mostRuns);

/** How many pairings that path made, which the cost is divided by. */
const pathSteps = new Float64Array(mostRuns * mostRuns);

/**
 * How unlike one written stroke is to one stored stroke.
 *
 * **The runs are aligned rather than paired off by position**, which is half of
 * what this function is for. A reader draws a 丿 as one straight sweep and the
 * outlines record it as three runs, the first of which is a six-unit lead-in at
 * the top. Compared position by position, the reader's whole stroke was being
 * measured against that lead-in. Aligned, it is the sweep against all three.
 *
 * The alignment is dynamic time warping with both ends pinned. Every run of
 * each stroke is used and their order is kept, which is what a stroke is. The
 * cost is divided by the number of pairings the path made, so a stroke of three
 * runs is not worth three times a stroke of one.
 *
 * Where the two strokes sit and how long they are is then asked once, of the
 * whole of each. See {@link costWeights}.
 */
export function strokeCost(written: Stroke, stored: Stroke): number {
  const rows = Math.min(written.runs.length, mostRuns);
  const columns = Math.min(stored.runs.length, mostRuns);

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const at = row * columns + column;
      const here = runCost(
        written.runs[row] as StrokeRun,
        written.length,
        stored.runs[column] as StrokeRun,
        stored.length,
      );

      if (row === 0 && column === 0) {
        pathCosts[at] = here;
        pathSteps[at] = 1;
        continue;
      }

      let best = Infinity;
      let steps = 1;

      for (const from of [
        row > 0 ? (row - 1) * columns + column : -1,
        column > 0 ? at - 1 : -1,
        row > 0 && column > 0 ? (row - 1) * columns + column - 1 : -1,
      ]) {
        if (from >= 0 && (pathCosts[from] as number) < best) {
          best = pathCosts[from] as number;
          steps = (pathSteps[from] as number) + 1;
        }
      }

      pathCosts[at] = best + here;
      pathSteps[at] = steps;
    }
  }

  const last = rows * columns - 1;
  const apart =
    Math.abs(written.column - stored.column) +
    Math.abs(written.row - stored.row);

  return (
    (pathCosts[last] as number) / (pathSteps[last] as number) +
    (apart / furthestPlace) * costWeights.place +
    (Math.abs(written.length - stored.length) / (shapeSteps - 1)) *
      costWeights.stroke
  );
}
